import { V, V2d } from "../Vector";

/** The golden ratio, approximately 1.618 */
export const GOLDEN_RATIO = 1.618;

/** Modulo operator that handles negative numbers correctly for modular arithmetic */
export function mod(a: number, b: number): number {
  return ((a % b) + b) % b;
}

/** Clamps a value to be non-negative (>= 0) */
export function clampUp(value: number) {
  return Math.max(value, 0);
}

/**
 * Clamps a value to be within the specified range
 * */
export function clamp(value: number, min = 0, max = 1): number {
  return Math.max(min, Math.min(max, value));
}

/** Smooth interpolation function that eases in and out. Useful for animations */
export function smoothStep(t: number): number {
  t = clamp(t);
  return 3 * t ** 2 - 2 * t ** 3;
}

/** Even smoother interpolation function with zero first and second derivatives at endpoints */
export function smootherStep(t: number): number {
  t = clamp(t);
  return 6 * t ** 5 - 15 * t ** 4 + 10 * t ** 3;
}

/** Linear interpolation between two values */
export function lerp(a: number, b: number, t: number = 0.5): number {
  return (1 - t) * a + t * b;
}

/** Inverse linear interpolation between two values */
export function ilerp(a: number, b: number, value: number): number {
  if (a === b) return 0; // Avoid division by zero
  return clamp((value - a) / (b - a));
}

/**
 * Convert from one linear range to another.
 */
export function lerpLerp(
  inputRange: [number, number],
  outputRange: [number, number],
  value: number,
): number {
  const t = ilerp(inputRange[0], inputRange[1], value);
  return lerp(outputRange[0], outputRange[1], t);
}

/** Linear interpolation between two 2D vectors */
export function lerpV2d(
  a: [number, number],
  b: [number, number],
  t: number = 0.5,
): V2d {
  const x = lerp(a[0], b[0], t);
  const y = lerp(a[1], b[1], t);
  return V(x, y);
}

/** Linear interpolation that snaps to the target when close enough */
export function lerpOrSnap(
  a: number,
  b: number,
  t: number = 0.5,
  threshold: number = 0.01,
): number {
  if (Math.abs(b - a) < threshold) {
    return b;
  }
  return lerp(a, b, t);
}

/** Normalizes an angle in radians to be in the range [-pi, pi] */
export function normalizeAngle(angle: number) {
  return mod(angle + Math.PI, Math.PI * 2) - Math.PI;
}

/** Returns the shortest angular difference between two angles in radians */
export function angleDelta(a: number, b: number): number {
  return mod(b - a + Math.PI, Math.PI * 2) - Math.PI;
}

/** Converts degrees to radians */
export function degToRad(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

/** Converts radians to degrees */
export function radToDeg(radians: number): number {
  return (radians * 180) / Math.PI;
}

/** Returns the result of reflecting an angle across the X axis. */
export function reflectX(theta: number): number {
  return normalizeAngle(Math.PI - theta);
}

/** Returns the result of reflecting an angle across the Y axis. */
export function reflectY(theta: number): number {
  return normalizeAngle(-theta);
}

/** Returns the result of reflecting an angle across both X and Y axes */
export function reflectXY(theta: number): number {
  return normalizeAngle(theta - Math.PI);
}

/**
 * Converts polar coordinates (angle and radius) to a Cartesian vector.
 * @param theta - Angle in radians (0 = east, π/2 = north)
 * @param r - Radius/magnitude
 * @returns V2d vector in Cartesian coordinates
 */
export function polarToVec(theta: number, r: number): V2d {
  return V(r * Math.cos(theta), r * Math.sin(theta));
}

/** Determines if a polygon's vertices are ordered counter-clockwise */
export function isCCW(points: readonly V2d[]): boolean {
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    const [x1, y1] = points[i - 1];
    const [x2, y2] = points[i];
    total += (x2 - x1) * (y2 + y1);
  }
  return total > 0;
}

/** Moves a value toward a target by a maximum step size. Useful for smooth value transitions */
export function stepToward(from: number, to: number, stepSize: number): number {
  if (to > from) {
    return Math.min(from + stepSize, to);
  } else {
    return Math.max(from - stepSize, to);
  }
}

/**
 * Converts a MIDI note number to frequency in Hz.
 * Note 60 = middle C (261.63 Hz), A4 = note 69 (440 Hz)
 */
export function noteToFrequency(noteNumber: number): number {
  return 440 * Math.pow(2, (noteNumber - 69) / 12);
}
